// MotivationAndBackground.js
import React from 'react';

export default function MotivationAndBackground() {
  return (
    <section className="py-20 bg-ink-50/60 border-t border-ink-100">
      <div className="max-w-5xl mx-auto px-6">
        <p className="section-eyebrow mb-3">02 &middot; Why It Matters</p>
        <h2 className="section-title">Motivation &amp; Background</h2>

        <div className="mt-10 grid md:grid-cols-2 gap-6">
          {/* Motivation */}
          <div className="card p-6 md:p-7">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-grad-brand-accent text-white grid place-items-center text-xs font-bold shadow-sm">
                M
              </div>
              <h3 className="text-xl font-semibold text-ink-900 font-serif">
                Motivation
              </h3>
            </div>
            <p className="mt-4 text-ink-600 leading-relaxed text-[0.98rem]">
              Although alignment methods like <strong>SFT</strong>,{' '}
              <strong>DPO</strong>, and <strong>RLHF</strong> are widely used
              to ensure LLM safety, recent research reveals these approaches
              are often <em>superficial</em>. Aligned models may look safe
              initially but still produce harmful content under adversarial
              prompts, subtle fine-tuning, or decoding manipulation &mdash;
              especially when the harmful content appears in the middle or end
              of a response, where current methods fail to maintain safety
              across the full generation process.
            </p>
          </div>

          {/* Background */}
          <div className="card p-6 md:p-7">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-grad-accent-brand text-white grid place-items-center text-xs font-bold shadow-sm">
                B
              </div>
              <h3 className="text-xl font-semibold text-ink-900 font-serif">
                Background
              </h3>
            </div>
            <p className="mt-4 text-ink-600 leading-relaxed text-[0.98rem]">
              Prior work suggests safety alignment can be interpreted as an{' '}
              <em>implicit</em> binary classification task. But safety-related
              signals get diluted by competing objectives (style, tone, human
              preferences), confusing the model under adversarial pressure.
              Inspired by BERT, we introduce an <strong>explicit</strong>{' '}
              safety classification task using a{' '}
              <code className="px-1.5 py-0.5 rounded bg-ink-100 text-ink-800 text-sm">[CLS]</code>{' '}
              token, combined with strategic attention and decoding &mdash;
              reinforcing clear decision boundaries and re-evaluating safety
              dynamically throughout generation.
            </p>
          </div>
        </div>

        {/* Contrast callout */}
        <div className="mt-10 max-w-3xl mx-auto">
          <div className="relative rounded-2xl bg-grad-callout p-[1px] shadow-card">
            <div className="rounded-2xl bg-white p-6 md:p-7">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-ink-500">
                    Existing alignment
                  </div>
                  <div className="mt-2 font-serif italic text-ink-700 leading-relaxed">
                    Implicit safety reasoning, signals diluted by competing
                    objectives, brittle under attack.
                  </div>
                </div>
                <div className="sm:border-l sm:border-ink-100 sm:pl-6">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-brand-600">
                    SA-ESS (ours)
                  </div>
                  <div className="mt-2 font-serif italic text-ink-800 leading-relaxed">
                    Explicit safety signal via{' '}
                    <code className="not-italic px-1 py-0.5 rounded bg-ink-100 text-ink-800 text-sm">[CLS]</code>,
                    re-evaluated at each generation step.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
